import { Container, Graphics } from 'pixi.js';
import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * GRAND COURT (Большой двор) — THE SOCIAL PLAZA, open-air. The lively heart of the castle where ducks bump
 * into each other between every wing. Composed as a single clean PROCESSIONAL: a royal carpet runner spills
 * from the Throne Hall's south door straight down the spine to the colossal tiered FOUNTAIN — flanked by a
 * tight guard-of-honour of two braziers + two heraldic banners + two sentinel statues — so standing at the
 * fountain and looking north frames the throne + hearth as the climactic sightline. The fountain (the hero)
 * sits on its own flagstone plaza, lit by a pair of lanterns so it reads as a glowing beacon even when empty.
 *
 * DECLUTTERED for legibility: the perimeter was a heap of competing micro-clusters (twin cafe nodes, a
 * lamp-post forest, a sundial, barrels/hay nooks, a redundant decorative bard stage). Now it breathes — ONE
 * cafe gather-node, the eight bench seats ringing the water, four corner trees framing the green, and a single
 * market-stall destination. Open grass is left as grass. Density reads as "designed courtyard", not litter.
 *
 * Geometry: zone (1180,1320) 1240×720 → centre (1800,1680). Doors kept clear at N@x1800, S@x1800, W@y1680,
 * E@y1680 — the central axes stay walkable (the runner is a flat decal; ducks flow around the fountain).
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art, mix, lighten, darken } = ctx;
  const cx = 1800;
  const cy = 1680;

  // ── A GRASS APRON + a circular flagstone PLAZA so the open-air court reads as outdoors with a deliberate
  //    gathering circle (laid first, under everything). The stone disc gives the fountain a landmark plaza
  //    to anchor the "meet me at the fountain" read. ──
  const grass = mix(0x4a7a3a, art.wall, 0.12);
  const flag = mix(0x7a6a52, art.wall, 0.4);
  const apron = new Container();
  apron.addChild(
    new Graphics()
      .roundRect(-588, -328, 1176, 656, 44).fill({ color: grass, alpha: 0.16 })
      .ellipse(0, 0, 230, 208).fill({ color: flag, alpha: 0.5 })
      .ellipse(0, 0, 230, 208).stroke({ color: mix(0xd9a63a, art.accent, 0.5), width: 3, alpha: 0.4 })
      .ellipse(0, 0, 150, 136).stroke({ color: lighten(flag, 0.1), width: 2, alpha: 0.4 }),
  );
  place(ctx, apron, cx, cy);
  void darken;

  // ── The PROCESSIONAL RUNNER from the throne's south door down to the fountain (laid first, under all). ──
  place(ctx, kit.runnerRug(art, 150, 340), cx, 1490);

  // ── GUARD OF HONOUR framing the approach: a tight pair of braziers + banners + sentinel statues. The
  //    braziers double as the fountain's warm glow so we don't need a lamp-post forest. ──
  place(ctx, kit.brazier(art), 1690, 1430);
  place(ctx, kit.brazier(art), 1910, 1430);
  place(ctx, kit.banner(art, 0x8a2f2f, 110), 1660, 1360);
  place(ctx, kit.banner(art, 0x2f4a8a, 110), 1940, 1360);
  place(ctx, kit.statue(art), 1610, 1500);
  place(ctx, kit.dragonStatue(art), 1990, 1500);

  // ── HERO: the colossal tiered fountain, dead-centre on the throne axis, with a single pair of lanterns to
  //    the south so it glows like a beacon — the unmistakable "meet me at the fountain" landmark. ──
  place(ctx, g.fountain(art, 122), cx, cy);
  place(ctx, g.lamppost(art), 1640, 1800);
  place(ctx, g.lamppost(art), 1960, 1800);

  // ── ONE cafe gather-node to the west — an accidental-bump cluster so the commons coalesces a crowd,
  //    balanced against the market stall to the east (one social pole each side, not a twin grid). ──
  place(ctx, kit.smallTable(art, 32), 1420, 1660);
  place(ctx, kit.woodStool(art), 1376, 1660);
  place(ctx, kit.woodStool(art), 1464, 1660);
  place(ctx, kit.woodStool(art), 1420, 1704);

  // ── The eight BENCH SEATS (the sit anchors): four diagonals facing the water + four quiet corner benches. ──
  for (const [bx, by] of [
    [1560, 1520],
    [2040, 1520],
    [1560, 1840],
    [2040, 1840],
    [1260, 1400],
    [1260, 1960],
    [2340, 1400],
    [2340, 1960],
  ] as const) {
    place(ctx, g.gardenBench(art), bx, by);
  }

  // ── ONE characterful destination: a market-stall spillover from the bailey in the SE. ──
  place(ctx, g.marketStall(art, 0x3a6a4a), 2180, 1860);

  // ── Trees anchoring the corners (a varied mix — never four identical oaks) + a little greenery off the
  //    door mouths. Open grass between them is left as grass. ──
  place(ctx, g.tree(art, 'oak'), 1260, 1380);
  place(ctx, g.tree(art, 'blossom'), 2340, 1380);
  place(ctx, g.tree(art, 'pine'), 1260, 1980);
  place(ctx, g.tree(art, 'oak'), 2340, 1980);
  place(ctx, g.flowerBed(art), 1430, 2000);
  place(ctx, g.flowerBed(art), 2170, 2000);
  place(ctx, g.topiary(art, 'cone'), 2230, 1560);
};

export default decorate;
